// client/src/utils/pokemonService.js
import axios from 'axios';

// Constants
const POKE_API_BASE = 'https://pokeapi.co/api/v2';

// Cache the full list so we only fetch once per session
let _allPokemon = null;

async function loadAllPokemon() {
  if (_allPokemon) return _allPokemon;
  const url = `${POKE_API_BASE}/pokemon?limit=20000`;
  const res = await axios.get(url);
  _allPokemon = res.data.results; // [{ name, url }, …]
  return _allPokemon;
}

/**
 * Search Pokémon by name fragment, then fetch their types & stats.
 * Now includes `id: name` to satisfy your TeamSchema requirement.
 */
export async function searchPokemon(query, limit = 10) {
  const all = await loadAllPokemon();
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const matched = all
    .filter(p => p.name.includes(q))
    .slice(0, limit);

  const detailed = await Promise.all(
    matched.map(async ({ name, url }) => {
      const { data } = await axios.get(url);
      return {
        // ❗️ Add this `id` field:
        id: data.name,               // e.g. "charizard"
        name: data.name,             // e.g. "charizard"
        types: data.types.map(t => t.type.name),
        stats: data.stats.reduce((acc, s) => {
          acc[s.stat.name] = s.base_stat;
          return acc;
        }, {}),
        imageUrl: data.sprites.front_default
      };
    })
  );

  return detailed;
}

/**
 * Fetch a Pokemon by ID or name
 * @param {string|number} idOrName - Pokemon ID or name
 * @returns {Promise<Object>} - Pokemon data
 */
export const getPokemon = async (idOrName) => {
  try {
    const { data } = await axios.get(`${POKE_API_BASE}/pokemon/${idOrName}`);
    return data;
  } catch (error) {
    console.error(`Error fetching Pokemon ${idOrName}:`, error);
    throw error;
  }
};

/**
 * Get popular Pokemon (predefined list)
 * @returns {Promise<Array>} - Array of popular Pokemon
 */
export const getPopularPokemon = async () => {
  try {
    // List of popular Pokemon IDs
    const popularIds = [1, 4, 7, 25, 133, 6, 9, 150, 151, 243, 244, 245];
    
    return await Promise.all(
      popularIds.map(async (id) => {
        const pokemon = await getPokemon(id);
        return {
          id: pokemon.id,
          name: pokemon.name,
          sprite: pokemon.sprites.other['official-artwork'].front_default || pokemon.sprites.front_default,
          regularSprite: pokemon.sprites.front_default,
          types: pokemon.types.map(t => t.type.name)
        };
      })
    );
  } catch (error) {
    console.error('Error fetching popular Pokemon:', error);
    throw error;
  }
};

/**
 * Get sprite URL for a Pokemon
 * @param {number} id - Pokemon ID
 * @param {boolean} useOfficialArt - Whether to use official artwork (default: true)
 * @returns {string} - Sprite URL
 */
export const getPokemonSpriteUrl = (id, useOfficialArt = true) => {
  if (!id) return null;
  
  if (useOfficialArt) {
    return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${id}.png`;
  }
  
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${id}.png`;
};

export default {
  searchPokemon,
  getPokemon,
  getPopularPokemon,
  getPokemonSpriteUrl
};